(() => {
  if (customElements.get('ivori-kids-story')) return;
  class IvoriKidsStory extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.options = {signal: this.controller.signal};
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.root = this.closest('[data-ivori-pdp]');
      this.frame = 0;
      this.scenes = new Set();
      this.setupColours();
      this.setupLens();
      this.setupVideo();
      this.setupScroll();
      this.setupPinnedScenes();
      this.querySelectorAll('[data-k-choose-colour],[data-k-choose-offer]').forEach(link => link.addEventListener('click', () => {
        const colour = link.dataset.kChooseColour;
        if (colour) {
          const control = [...this.root.querySelectorAll('[data-ivori-color]')].find(node => node.dataset.ivoriColor === colour);
          if (control?.getAttribute('aria-pressed') !== 'true') control?.click();
        }
        const offer = link.dataset.kChooseOffer;
        if (offer) {
          const control = this.root.querySelector(`[data-ivori-offer][value="${offer}"]`);
          if (control && !control.checked) control.click();
        }
      }, this.options));
      this.classList.add('k-enhanced');
    }
    setupColours() {
      const controls = this.querySelector('[data-k-colour-controls]');
      const buttons = [...this.querySelectorAll('[data-k-colour-button]')];
      const panels = [...this.querySelectorAll('[data-k-colour-panel]')];
      const choose = key => {
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.kColourButton === key)));
        panels.forEach(panel => {
          const active = panel.dataset.kColourPanel === key;
          panel.hidden = !active;
          if (active && !this.reduced.matches && panel.animate) panel.animate([{opacity: .5, transform: 'translateY(12px)'}, {opacity: 1, transform: 'none'}], {duration: 420, easing: 'ease-out'});
        });
        const layout = this.querySelector('[data-k-colour]');
        if (layout) layout.dataset.kColour = key;
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => choose(button.dataset.kColourButton), this.options);
        button.addEventListener('keydown', event => {
          if (!['ArrowRight','ArrowLeft','ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
          event.preventDefault();
          let next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) % buttons.length;
          buttons[next].focus(); choose(buttons[next].dataset.kColourButton);
        }, this.options);
      });
      if (controls) controls.hidden = false;
      choose('blue');
    }
    setupLens() {
      this.lensBox = this.querySelector('[data-k-lens-box]');
      this.lens = this.querySelector('[data-k-lens]');
      this.lensControl = this.querySelector('[data-k-lens-control]');
      this.lensInput = this.lensControl?.querySelector('input');
      if (!this.lensBox || !this.lens || !this.lensInput) return;
      this.lens.hidden = false; this.lensControl.hidden = false;
      this.lensImage = this.lens.querySelector('img');
      this.lensPoint = {x: .28, y: .36};
      this.lensPointer = null;
      this.lensBox.querySelectorAll('img').forEach(image => image.draggable = false);
      const explore = event => {
        const rect = this.lensBox.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        this.lensPoint = {x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)), y: event.pointerType === 'touch' ? .36 : Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))};
        this.drawLens();
      };
      this.lensBox.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.pointerType === 'mouse' && event.button !== 0) return;
        this.lensManual = true;
        this.lensPointer = event.pointerId; explore(event); this.lensBox.setPointerCapture(event.pointerId);
      }, {...this.options, passive:true});
      this.lensBox.addEventListener('pointermove', event => {
        if (!event.isPrimary || event.pointerType === 'touch' && this.lensPointer !== event.pointerId || this.pinEnabled && this.lensPointer === null) return;
        explore(event);
      }, {...this.options, passive:true});
      ['pointerup','pointercancel','lostpointercapture'].forEach(type => this.lensBox.addEventListener(type, event => {
        if (this.lensPointer !== event.pointerId) return;
        this.lensPointer = null;
        if (event.type === 'pointercancel') this.lensManual = false;
        if (this.lensBox.hasPointerCapture(event.pointerId)) this.lensBox.releasePointerCapture(event.pointerId);
      }, this.options));
      this.lensInput.addEventListener('input', event => {event.stopPropagation(); this.lensManual = true; this.lensPoint = {x: Number(event.target.value) / 100, y: .36}; this.drawLens();}, this.options);
      this.lensInput.addEventListener('change', event => event.stopPropagation(), this.options);
      if ('ResizeObserver' in window) {this.lensResize = new ResizeObserver(() => this.drawLens()); this.lensResize.observe(this.lensBox);}
      this.drawLens();
    }
    drawLens() {
      if (!this.lensBox || !this.lensImage) return;
      const width = this.lensBox.clientWidth, height = this.lensBox.clientHeight, radius = this.lens.clientWidth / 2;
      if (!width || !height) return;
      const x = Math.max(radius, Math.min(width - radius, width * this.lensPoint.x));
      const y = Math.max(radius, Math.min(height - radius, height * this.lensPoint.y));
      this.lens.style.transform = `translate3d(${x-radius}px,${y-radius}px,0)`;
      this.lensImage.style.width = `${width * 2.1}px`;
      this.lensImage.style.transform = `translate3d(${radius-x*2.1}px,${radius-y*2.1}px,0)`;
      if (document.activeElement !== this.lensInput) this.lensInput.value = String(Math.round(this.lensPoint.x * 100));
    }
    setupVideo() {
      this.video = this.querySelector('[data-k-video]');
      this.videoButton = this.querySelector('[data-k-video-toggle]');
      if (!this.video || !this.videoButton) return;
      this.videoVisible = false; this.videoPausedByUser = false; this.videoManual = false;
      const source = this.video.querySelector('source');
      this.loadVideo = () => {
        if (source && !source.src) {source.src = source.dataset.kVideoSource; this.video.load();}
      };
      const label = () => {this.videoButton.textContent = this.video.paused ? this.dataset.playLabel : this.dataset.pauseLabel;};
      this.videoButton.hidden = false; this.video.controls = false;
      this.video.addEventListener('play', label, this.options); this.video.addEventListener('pause', label, this.options);
      this.video.addEventListener('error', () => {this.video.controls = true; label();}, this.options);
      this.videoButton.addEventListener('click', () => {
        if (!this.video.paused) {this.videoPausedByUser = true; this.video.pause();}
        else {this.videoPausedByUser = false; this.videoManual = true; this.loadVideo(); this.video.play().catch(() => {this.video.controls = true;});}
      }, this.options);
      this.syncVideo = () => {
        const saveData = navigator.connection?.saveData;
        if (!this.videoVisible || document.hidden || this.videoPausedByUser || (this.reduced.matches || saveData) && !this.videoManual) this.video.pause();
        else {this.loadVideo(); this.video.play().catch(() => {});}
      };
      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver(entries => {this.videoVisible = entries[0].isIntersecting; this.syncVideo();}, {threshold:.18}); this.videoObserver.observe(this.video);
      }
      document.addEventListener('visibilitychange', this.syncVideo, this.options);
      this.reduced.addEventListener('change', () => {this.videoManual = false; this.syncVideo();}, this.options);
    }
    setupScroll() {
      const reveal = [...this.querySelectorAll('.k-reveal')];
      if ('IntersectionObserver' in window) {
        this.revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) {entry.target.classList.remove('k-pending'); this.revealObserver.unobserve(entry.target);}
        }), {threshold:.1,rootMargin:'0px 0px 30px 0px'});
        reveal.forEach(node => {if (!this.reduced.matches && node.getBoundingClientRect().top > innerHeight) node.classList.add('k-pending'); this.revealObserver.observe(node);});
        this.sceneObserver = new IntersectionObserver(entries => {entries.forEach(entry => entry.isIntersecting ? this.scenes.add(entry.target) : this.scenes.delete(entry.target)); this.schedule();}, {rootMargin:'100px 0px'});
        this.querySelectorAll('[data-k-scene]').forEach(node => this.sceneObserver.observe(node));
      }
      this.schedule = () => {if (!this.frame) this.frame = requestAnimationFrame(() => this.paint());};
      window.addEventListener('scroll', this.schedule, {...this.options,passive:true});
      window.addEventListener('resize', this.schedule, {...this.options,passive:true});
      this.reduced.addEventListener('change', () => {if (this.reduced.matches) reveal.forEach(node => node.classList.remove('k-pending')); this.schedule();}, this.options);
      this.schedule();
    }
    setupPinnedScenes() {
      this.pinScenes = [...this.querySelectorAll('[data-k-pin]')].map(node => ({node,stage:node.querySelector('[data-k-stage]'),kind:node.dataset.kPin,beats:[...node.querySelectorAll('[data-k-beat]')],progress:[...node.querySelectorAll('[data-k-progress]')]}));
      const measure = () => {
        const header = document.querySelector('.header-wrapper');
        const height = Math.min(150,Math.max(56,header?.getBoundingClientRect().height || 72));
        this.style.setProperty('--k-pin-top',`${height}px`);
        this.pinEnabled = !this.reduced.matches && innerHeight >= 500;
        this.classList.toggle('k-pin-active',this.pinEnabled);
        this.pinScenes.forEach(scene => {
          scene.beats.forEach((beat,index) => {if(index) beat.hidden = !this.pinEnabled; if(!this.pinEnabled) {beat.style.opacity='';beat.style.transform='';}});
          const progress = scene.node.querySelector('.k-scroll-progress'); if(progress) progress.hidden = !this.pinEnabled;
        });
        this.schedule();
      };
      window.addEventListener('resize',measure,{...this.options,passive:true});
      this.reduced.addEventListener('change',measure,this.options);
      measure();
    }
    paintPins() {
      if (!this.pinScenes) return;
      const clamp = n => Math.max(0,Math.min(1,n));
      const ease = (a,b,n) => {const x=clamp((n-a)/(b-a));return x*x*(3-2*x);};
      this.pinScenes.forEach(scene => {
        const rect=scene.node.getBoundingClientRect();
        if(rect.bottom < -150 || rect.top > innerHeight+150) return;
        const top=parseFloat(getComputedStyle(scene.stage).top)||0;
        const p=this.pinEnabled ? clamp((top-rect.top)/Math.max(1,rect.height-scene.stage.offsetHeight)) : 0;
        scene.node.dataset.scrollProgress=p.toFixed(3);
        if(scene.kind==='hello') {
          const gather=ease(.04,.47,p),close=ease(.50,.94,p);
          const vars={'--k-main-scale':1+gather*.22+close*.85,'--k-main-rotate':`${-6+gather*6}deg`,'--k-main-y':`${close*8}px`,'--k-left-x':`${gather*-150}px`,'--k-right-x':`${gather*150}px`,'--k-side-y':`${gather*60}px`,'--k-left-rotate':`${-12-gather*24}deg`,'--k-right-rotate':`${12+gather*24}deg`,'--k-side-opacity':1-ease(.25,.52,p)};
          Object.entries(vars).forEach(([key,value])=>scene.stage.style.setProperty(key,String(value)));
          const opacity=[1-ease(.2,.32,p),ease(.22,.35,p)*(1-ease(.57,.7,p)),ease(.59,.73,p)];
          if(this.pinEnabled) scene.beats.forEach((beat,index)=>{beat.style.opacity=opacity[index].toFixed(3);beat.style.transform=`translate3d(0,${(1-opacity[index])*22}px,0)`;});
          scene.progress.forEach((bar,index)=>{bar.style.setProperty('--k-progress',clamp(p*3-index));bar.dataset.current=String(Math.min(2,Math.floor(p*3))===index);});
        } else {
          scene.stage.style.setProperty('--k-detail-scale',String(1+ease(.05,.75,p)*.08));
          scene.stage.style.setProperty('--k-detail-copy-y',`${(1-ease(.1,.5,p))*20}px`);
          scene.stage.style.setProperty('--k-detail-copy-opacity',String(.5+ease(.1,.5,p)*.5));
          if(this.pinEnabled && !this.lensManual) {this.lensPoint={x:.28+ease(.1,.88,p)*.44,y:.36};this.drawLens();}
        }
      });
      const trio=this.querySelector('.k-trio-photo');
      if(trio){const r=trio.getBoundingClientRect();const p=clamp((innerHeight-r.top)/(innerHeight*.85));trio.style.setProperty('--k-trio-scale',this.reduced.matches?'1':String(.88+p*.12));}
    }
    paint() {
      this.frame = 0;
      if (!this.isConnected || document.hidden) return;
      this.paintPins();
      this.scenes.forEach(scene => {
        const rect = scene.getBoundingClientRect();
        const progress = Math.max(-1,Math.min(1,(innerHeight * .5 - rect.top - rect.height * .5) / innerHeight));
        scene.querySelectorAll('[data-k-float]').forEach(node => node.style.setProperty('--k-float', `${this.reduced.matches ? 0 : progress * Number(node.dataset.kFloat) * 30}px`));
      });
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      cancelAnimationFrame(this.frame);
      this.revealObserver?.disconnect(); this.sceneObserver?.disconnect(); this.videoObserver?.disconnect(); this.lensResize?.disconnect();
      this.video?.pause();
    }
  }
  customElements.define('ivori-kids-story', IvoriKidsStory);
})();
